/**
 * Test script to verify Mews API connectivity
 * 
 * Tests all three configuration endpoints:
 * - getConfiguration()
 * - getHotels()
 * - getPaymentConfiguration()
 * 
 * Usage:
 *   Import and call testMewsApiConnection() from your code
 *   or run in browser console after importing
 */

import {
  getConfiguration,
  getHotels,
  getPaymentConfiguration,
  getMewsApiConfig,
  type ApiResponse,
  convertToOccupancies,
  mapLocalRoomToMews,
  mapProductsToUpsells,
  mapMewsRateToLocalRatePlan,
  autoMatchRooms,
  setRoomMapping,
  loadRoomMappingFromEnv,
  getMewsRoomCategoryId,
  getRoomMapping,
  getAvailability,
  getReservationsPricing,
  getServicesAvailability,
  getServicesPricing,
  type OccupancyData,
  type Occupancy,
} from './index';
import type {
  ConfigurationResponse,
  HotelsResponse,
  PaymentConfigurationResponse,
  AvailabilityResponse,
  ReservationsPricingResponse,
  ServicesAvailabilityResponse,
  ServicesPricingResponse,
} from './types';

/**
 * Test results interface
 */
export interface TestResults {
  configTest: {
    success: boolean;
    error?: string;
    data?: ConfigurationResponse;
  };
  hotelsTest: {
    success: boolean;
    error?: string;
    data?: HotelsResponse;
  };
  paymentConfigTest: {
    success: boolean;
    error?: string;
    data?: PaymentConfigurationResponse;
  };
  mappingTests?: {
    occupancyConversion: {
      success: boolean;
      error?: string;
      testCases?: Array<{
        adults: number;
        children: number;
        resultCount: number;
        hasAdult: boolean;
        hasChild: boolean;
      }>;
    };
    roomMapping: {
      success: boolean;
      error?: string;
      totalRooms: number;
      matchedRooms: number;
      matchRate: number;
      matchedDetails?: Array<{
        localId: string;
        mewsId: string;
        mewsName: string;
        score?: number;
        source?: 'manual' | 'auto';
      }>;
      unmatchedRooms?: string[];
    };
    productMapping: {
      success: boolean;
      error?: string;
      totalProducts: number;
      mappedProducts: number;
      skippedProducts: number;
      mappedDetails?: Array<{
        id: string;
        name: string;
        category: string;
        price: number | null;
      }>;
    };
    ratePlanMapping: {
      success: boolean;
      error?: string;
      testCases?: Array<{
        rateId: string;
        rateName: string;
        mappedTo: string | null;
        isMapped?: boolean; // Whether this rate has a manual mapping configured
      }>;
    };
  };
  phase3Tests?: {
    availability: {
      success: boolean;
      error?: string;
      data?: AvailabilityResponse;
      requestParams?: {
        startUtc: string;
        endUtc: string;
        occupancyData: OccupancyData[];
      };
    };
    reservationsPricing: {
      success: boolean;
      error?: string;
      data?: ReservationsPricingResponse;
      requestParams?: {
        startUtc: string;
        endUtc: string;
        roomCategoryId: string;
        occupancies: Occupancy[];
      };
    };
    servicesAvailability: {
      success: boolean;
      error?: string;
      data?: ServicesAvailabilityResponse;
      requestParams?: {
        startUtc: string;
        endUtc: string;
        serviceId: string;
      };
    };
    servicesPricing: {
      success: boolean;
      error?: string;
      data?: ServicesPricingResponse;
      requestParams?: {
        startUtc: string;
        endUtc: string;
        serviceId: string;
      };
    };
  };
}

/**
 * Tests Mews API connection and all configuration endpoints
 * 
 * @returns Promise resolving to test results
 * 
 * @example
 * ```typescript
 * const results = await testMewsApiConnection();
 * console.log('Configuration test:', results.configTest.success);
 * console.log('Hotels test:', results.hotelsTest.success);
 * console.log('Payment config test:', results.paymentConfigTest.success);
 * ```
 */
export async function testMewsApiConnection(): Promise<TestResults> {
  const results: TestResults = {
    configTest: { success: false },
    hotelsTest: { success: false },
    paymentConfigTest: { success: false },
  };

  console.log('🔍 Testing Mews API Connection...\n');

  // Test 1: Validate environment configuration
  try {
    const config = getMewsApiConfig();
    console.log('✅ Environment configuration valid');
    console.log(`   Base URL: ${config.baseUrl}`);
    console.log(`   Client Identifier: ${config.clientIdentifier}`);
    if (config.hotelId) console.log(`   Hotel ID: ${config.hotelId}`);
    if (config.configurationId) console.log(`   Configuration ID: ${config.configurationId}`);
    console.log('');
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ Environment configuration error:', errorMessage);
    console.error('   Please check your .env.local file\n');
    return results;
  }

  // Test 2: Get Configuration
  console.log('📋 Testing getConfiguration()...');
  try {
    const configResponse: ApiResponse<ConfigurationResponse> = await getConfiguration(false);
    
    if (configResponse.error) {
      // Show full error details for debugging
      const errorMsg = configResponse.error.Message;
      results.configTest.error = errorMsg;
      console.error(`   ❌ Failed: ${errorMsg}`);
      if (configResponse.error.Code) {
        console.error(`   Code: ${configResponse.error.Code}`);
      }
      if (configResponse.error.Details) {
        console.error(`   Details:`, configResponse.error.Details);
        // If Details contains the raw response, show it
        if (configResponse.error.Details.responseBody) {
          console.error(`   Raw API Response:`, configResponse.error.Details.responseBody);
        }
      }
      // Also log the full response for debugging
      console.error(`   Full Error Object:`, configResponse.error);
      console.error(`   Response Status:`, configResponse.status);
    } else if (configResponse.data) {
      results.configTest.success = true;
      results.configTest.data = configResponse.data;
      console.log('   ✅ Success');
      console.log(`   Age Categories: ${configResponse.data.AgeCategories?.length || 0}`);
      console.log(`   Policies: ${configResponse.data.Policies?.length || 0}`);
      if (configResponse.data.Currency) {
        console.log(`   Currency: ${configResponse.data.Currency}`);
      }
      if (configResponse.data.CheckInTime) {
        console.log(`   Check-in: ${configResponse.data.CheckInTime}`);
      }
      if (configResponse.data.CheckOutTime) {
        console.log(`   Check-out: ${configResponse.data.CheckOutTime}`);
      }
    } else {
      results.configTest.error = 'No data returned';
      console.error('   ❌ No data returned');
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    results.configTest.error = errorMessage;
    console.error(`   ❌ Error: ${errorMessage}`);
  }
  console.log('');

  // Test 3: Get Hotels
  console.log('🏨 Testing getHotels()...');
  try {
    const hotelsResponse: ApiResponse<HotelsResponse> = await getHotels(undefined, false);
    
    if (hotelsResponse.error) {
      results.hotelsTest.error = hotelsResponse.error.Message;
      console.error(`   ❌ Failed: ${hotelsResponse.error.Message}`);
      if (hotelsResponse.error.Code) {
        console.error(`   Code: ${hotelsResponse.error.Code}`);
      }
      if (hotelsResponse.error.Details) {
        console.error(`   Details:`, hotelsResponse.error.Details);
      }
    } else if (hotelsResponse.data) {
      results.hotelsTest.success = true;
      results.hotelsTest.data = hotelsResponse.data;
      console.log('   ✅ Success');
      // Hotel name is localized, try to get English or first available
      const getName = (name: unknown): string => {
        if (!name) return 'Unknown';
        if (typeof name === 'string') return name;
        if (typeof name === 'object' && name !== null) {
          const nameObj = name as Record<string, unknown>;
          const enName = nameObj['en-US'];
          if (typeof enName === 'string') return enName;
          const firstValue = Object.values(nameObj)[0];
          return typeof firstValue === 'string' ? firstValue : 'Unknown';
        }
        return 'Unknown';
      };
      const hotelName = getName(hotelsResponse.data.Name);
      console.log(`   Hotel: ${hotelName}`);
      console.log(`   Room Categories: ${hotelsResponse.data.RoomCategories?.length || 0}`);
      if (hotelsResponse.data.RoomCategories && hotelsResponse.data.RoomCategories.length > 0) {
        console.log('   Sample room categories:');
        hotelsResponse.data.RoomCategories.slice(0, 3).forEach((room) => {
          const getName = (name: unknown): string => {
            if (!name) return 'Unknown';
            if (typeof name === 'string') return name;
            if (typeof name === 'object' && name !== null) {
              const nameObj = name as Record<string, unknown>;
              const enName = nameObj['en-US'];
              if (typeof enName === 'string') return enName;
              const firstValue = Object.values(nameObj)[0];
              return typeof firstValue === 'string' ? firstValue : 'Unknown';
            }
            return 'Unknown';
          };
          const roomName = getName(room.Name);
          console.log(`     - ${roomName} (ID: ${room.Id})`);
        });
      }
      if (hotelsResponse.data.Products && hotelsResponse.data.Products.length > 0) {
        console.log(`   Products: ${hotelsResponse.data.Products.length}`);
      }
    } else {
      results.hotelsTest.error = 'No data returned';
      console.error('   ❌ No data returned');
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    results.hotelsTest.error = errorMessage;
    console.error(`   ❌ Error: ${errorMessage}`);
  }
  console.log('');

  // Test 4: Get Payment Configuration
  console.log('💳 Testing getPaymentConfiguration()...');
  try {
    const paymentResponse: ApiResponse<PaymentConfigurationResponse> = await getPaymentConfiguration(undefined, false);
    
    if (paymentResponse.error) {
      results.paymentConfigTest.error = paymentResponse.error.Message;
      console.error(`   ❌ Failed: ${paymentResponse.error.Message}`);
      if (paymentResponse.error.Code) {
        console.error(`   Code: ${paymentResponse.error.Code}`);
      }
      if (paymentResponse.error.Details) {
        console.error(`   Details:`, paymentResponse.error.Details);
      }
    } else if (paymentResponse.data) {
      results.paymentConfigTest.success = true;
      results.paymentConfigTest.data = paymentResponse.data;
      console.log('   ✅ Success');
      if (paymentResponse.data.PaymentGateway) {
        console.log(`   Payment Gateway: ${paymentResponse.data.PaymentGateway.PaymentGatewayType}`);
        console.log(`   Is Merchant: ${paymentResponse.data.PaymentGateway.IsMerchant}`);
        console.log(`   Supported Cards: ${paymentResponse.data.PaymentGateway.SupportedCreditCardTypes?.join(', ') || 'None'}`);
        if (paymentResponse.data.PaymentGateway.DefaultCurrencyCode) {
          console.log(`   Default Currency: ${paymentResponse.data.PaymentGateway.DefaultCurrencyCode}`);
        }
      }
      if (paymentResponse.data.SurchargeConfiguration) {
        console.log(`   Surcharge Service ID: ${paymentResponse.data.SurchargeConfiguration.SurchargeServiceId}`);
        if (paymentResponse.data.SurchargeConfiguration.SurchargeFees) {
          const fees = Object.entries(paymentResponse.data.SurchargeConfiguration.SurchargeFees)
            .map(([card, fee]) => `${card}: ${(fee * 100).toFixed(2)}%`)
            .join(', ');
          console.log(`   Surcharge Fees: ${fees}`);
        }
      }
    } else {
      results.paymentConfigTest.error = 'No data returned';
      console.error('   ❌ No data returned');
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    results.paymentConfigTest.error = errorMessage;
    console.error(`   ❌ Error: ${errorMessage}`);
  }
  console.log('');

  // Test 5: Mapping Functions (only if API tests passed)
  if (results.configTest.success && results.hotelsTest.success) {
    console.log('🗺️  Testing Mapping Functions...\n');
    results.mappingTests = await testMappingFunctions(
      results.configTest.data,
      results.hotelsTest.data
    );
    console.log('');
  }

  // Test 6: Phase 3 - Availability & Pricing (only if all previous tests passed)
  if (results.configTest.success && results.hotelsTest.success) {
    console.log('📅 Testing Phase 3: Availability & Pricing APIs...\n');
    results.phase3Tests = await testPhase3Apis(
      results.configTest.data,
      results.hotelsTest.data
    );
    console.log('');
    
    // Update rate plan mapping test with real rate data from Phase 3
    if (results.mappingTests && results.phase3Tests?.availability.success) {
      console.log('💰 Updating Rate Plan Mapping with real API data...\n');
      await updateRatePlanMappingWithRealData(results.mappingTests, results.phase3Tests);
      console.log('');
    }
  }

  // Summary
  const allPassed = 
    results.configTest.success &&
    results.hotelsTest.success &&
    results.paymentConfigTest.success;

  if (allPassed) {
    console.log('✨ All API tests passed! Mews API connection is working correctly.');
    if (results.mappingTests) {
      const mappingAllPassed = 
        results.mappingTests.occupancyConversion.success &&
        results.mappingTests.roomMapping.success &&
        results.mappingTests.productMapping.success &&
        results.mappingTests.ratePlanMapping.success;
      
      if (mappingAllPassed) {
        console.log('✨ All mapping tests passed! Mapping functions are working correctly.');
      } else {
        console.log('⚠️  Some mapping tests had issues. Review the details above.');
      }
    }
  } else {
    console.error('❌ Some tests failed. Please check the errors above.');
    console.error('\n💡 Troubleshooting tips:');
    console.error('   1. Verify your .env.local file has correct values');
    console.error('   2. Check that VITE_MEWS_API_BASE_URL is correct');
    console.error('   3. Verify VITE_MEWS_HOTEL_ID is a valid UUID');
    console.error('   4. Check your internet connection');
    console.error('   5. Verify API credentials with Mews support');
  }

  return results;
}

/**
 * Tests mapping functions with real API data
 * 
 * @param configData - Configuration response data
 * @param hotelsData - Hotels response data
 * @returns Mapping test results
 */
async function testMappingFunctions(
  configData: ConfigurationResponse | undefined,
  hotelsData: HotelsResponse | undefined
): Promise<TestResults['mappingTests']> {
  const results: TestResults['mappingTests'] = {
    occupancyConversion: { success: false },
    roomMapping: { success: false, totalRooms: 0, matchedRooms: 0, matchRate: 0 },
    productMapping: { success: false, totalProducts: 0, mappedProducts: 0, skippedProducts: 0 },
    ratePlanMapping: { success: false },
  };

  // Test 1: Occupancy Conversion
  console.log('📊 Testing Occupancy Conversion...');
  try {
    if (!configData?.AgeCategories || configData.AgeCategories.length === 0) {
      results.occupancyConversion.error = 'No age categories available from Configuration API';
      console.error('   ❌ No age categories available');
    } else {
      // Log all age categories for debugging
      console.log('   📋 Age categories from API:');
      configData.AgeCategories.forEach((cat, idx) => {
        // Handle localized names
        const getName = (name: string | Record<string, string>): string => {
          if (typeof name === 'string') return name;
          return name['en-US'] || name['de-CH'] || name['de-DE'] || Object.values(name)[0] || 'Unknown';
        };
        const displayName = getName(cat.Name);
        console.log(`      ${idx + 1}. "${displayName}" (ID: ${cat.Id.substring(0, 8)}...) - Min: ${cat.MinimumAge ?? 'N/A'}, Max: ${cat.MaximumAge ?? 'N/A'}`);
      });
      console.log('');
      const testCases = [
        { adults: 2, children: 1 },
        { adults: 1, children: 0 },
        { adults: 2, children: 2 },
      ];

      const testResults: Array<{
        adults: number;
        children: number;
        resultCount: number;
        hasAdult: boolean;
        hasChild: boolean;
      }> = [];

      // Helper to extract localized name
      const getName = (name: string | Record<string, string>): string => {
        if (typeof name === 'string') return name;
        return name['en-US'] || name['de-CH'] || name['de-DE'] || Object.values(name)[0] || 'Unknown';
      };

      for (const testCase of testCases) {
        const occupancies = convertToOccupancies(
          testCase.adults,
          testCase.children,
          configData.AgeCategories
        );

        const hasAdult = occupancies.some(occ => {
          const category = configData.AgeCategories.find(c => c.Id === occ.AgeCategoryId);
          if (!category) return false;
          const name = getName(category.Name);
          const nameLower = name.toLowerCase();
          return (
            nameLower.includes('adult') ||
            nameLower.includes('erwachsener') ||
            nameLower.includes('erwachsene') ||
            (category.MinimumAge !== undefined && category.MinimumAge >= 18)
          );
        });

        // Find adult category ID
        const adultCategoryId = occupancies.find(occ => {
          const category = configData.AgeCategories.find(c => c.Id === occ.AgeCategoryId);
          if (!category) return false;
          const name = getName(category.Name);
          const nameLower = name.toLowerCase();
          return (
            nameLower.includes('adult') ||
            nameLower.includes('erwachsener') ||
            nameLower.includes('erwachsene') ||
            (category.MinimumAge !== undefined && category.MinimumAge >= 18)
          );
        })?.AgeCategoryId;

        const hasChild = testCase.children > 0 && occupancies.some(occ => {
          if (occ.AgeCategoryId === adultCategoryId) return false;
          const category = configData.AgeCategories.find(c => c.Id === occ.AgeCategoryId);
          return category !== undefined;
        });

        testResults.push({
          adults: testCase.adults,
          children: testCase.children,
          resultCount: occupancies.length,
          hasAdult,
          hasChild: testCase.children === 0 || hasChild,
        });
      }

      const allPassed = testResults.every(
        r => r.resultCount > 0 && r.hasAdult && (r.children === 0 || r.hasChild)
      );

      if (allPassed) {
        results.occupancyConversion.success = true;
        results.occupancyConversion.testCases = testResults;
        console.log('   ✅ Success');
        testResults.forEach((test, idx) => {
          console.log(
            `   Test case ${idx + 1}: ${test.adults} adults, ${testCases[idx].children} children → ${test.resultCount} age categories`
          );
        });
      } else {
        results.occupancyConversion.error = 'Some test cases failed';
        console.error('   ❌ Some test cases failed');
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    results.occupancyConversion.error = errorMessage;
    console.error(`   ❌ Error: ${errorMessage}`);
  }
  console.log('');

  // Test 2: Room Mapping
  console.log('🏠 Testing Room Mapping...');
  try {
    if (!hotelsData?.RoomCategories || hotelsData.RoomCategories.length === 0) {
      results.roomMapping.error = 'No room categories available from Hotels API';
      console.error('   ❌ No room categories available');
    } else {
      // Extract local room IDs from ROOM_LADDER structure
      // These are the known room IDs from the booking page (src/pages/Book.tsx)
      const localRoomIds = [
        'junior-suite',
        'family-suite',
        'superior-double-room',
        'double-twin-balcony-lake',
        'double-twin-balcony-village',
        'double-twin-noBal',
        'standard-triple-room',
        'single-balcony-lake',
        'heritage-single',
        'basic-single',
      ];

      // Ensure any manual mappings from environment are loaded
      loadRoomMappingFromEnv();

      // Attempt auto-matching as a fallback
      const languageCode = hotelsData.DefaultLanguageCode || 'en-US';
      const autoMatched = autoMatchRooms(localRoomIds, hotelsData.RoomCategories, languageCode);

      // Merge auto-matched mapping into current mapping (manual/env mappings take precedence)
      const currentMapping = getRoomMapping();
      if (Object.keys(autoMatched).length > 0) {
        setRoomMapping({ ...autoMatched, ...currentMapping });
      }

      // Get details of matched rooms
      const matchedDetails: Array<{
        localId: string;
        mewsId: string;
        mewsName: string;
        score?: number;
        source?: 'manual' | 'auto';
      }> = [];

      const unmatchedRooms: string[] = [];

      for (const localId of localRoomIds) {
        // First, check explicit/manual mapping
        const manualId = getMewsRoomCategoryId(localId);
        const autoId = autoMatched[localId];
        const mewsId = manualId || autoId;

        if (mewsId) {
          const mewsRoom = hotelsData.RoomCategories.find(r => r.Id === mewsId);
          const mewsName = mewsRoom
            ? (mewsRoom.Name[languageCode] || Object.values(mewsRoom.Name)[0] || 'Unknown')
            : 'Unknown';
          matchedDetails.push({
            localId,
            mewsId,
            mewsName: mewsName,
            source: manualId ? 'manual' : 'auto',
          });
        } else {
          unmatchedRooms.push(localId);
        }
      }

      const matchRate = (matchedDetails.length / localRoomIds.length) * 100;

      results.roomMapping.success = true;
      results.roomMapping.totalRooms = localRoomIds.length;
      results.roomMapping.matchedRooms = matchedDetails.length;
      results.roomMapping.matchRate = matchRate;
      results.roomMapping.matchedDetails = matchedDetails;
      results.roomMapping.unmatchedRooms = unmatchedRooms;

      console.log(`   ✅ Success: ${matchedDetails.length}/${localRoomIds.length} rooms matched (${matchRate.toFixed(1)}%)`);
      
      if (matchedDetails.length > 0) {
        console.log('   Matched rooms:');
        matchedDetails.slice(0, 5).forEach(detail => {
          console.log(`     - ${detail.localId} → "${detail.mewsName}" (ID: ${detail.mewsId.substring(0, 8)}...)`);
        });
        if (matchedDetails.length > 5) {
          console.log(`     ... and ${matchedDetails.length - 5} more`);
        }
      }

      if (unmatchedRooms.length > 0) {
        console.log(`   ⚠️  Unmatched rooms (${unmatchedRooms.length}): ${unmatchedRooms.slice(0, 3).join(', ')}${unmatchedRooms.length > 3 ? '...' : ''}`);
        console.log('      Consider manual mapping for these rooms');
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    results.roomMapping.error = errorMessage;
    console.error(`   ❌ Error: ${errorMessage}`);
  }
  console.log('');

  // Test 3: Product Mapping
  console.log('🛍️  Testing Product Mapping...');
  try {
    if (!hotelsData?.Products || hotelsData.Products.length === 0) {
      results.productMapping.error = 'No products available from Hotels API';
      console.error('   ❌ No products available');
    } else {
      const languageCode = hotelsData.DefaultLanguageCode || 'en-US';
      const upsells = mapProductsToUpsells(
        hotelsData.Products,
        languageCode,
        hotelsData.ImageBaseUrl
      );

      const mappedDetails = upsells.map(upsell => ({
        id: upsell.id,
        name: upsell.name,
        category: upsell.category,
        price: upsell.price,
      }));

      const totalProducts = hotelsData.Products.length;
      const mappedProducts = upsells.length;
      const skippedProducts = totalProducts - mappedProducts;

      results.productMapping.success = true;
      results.productMapping.totalProducts = totalProducts;
      results.productMapping.mappedProducts = mappedProducts;
      results.productMapping.skippedProducts = skippedProducts;
      results.productMapping.mappedDetails = mappedDetails;

      console.log(`   ✅ Success: ${mappedProducts}/${totalProducts} products mapped (${((mappedProducts / totalProducts) * 100).toFixed(1)}%)`);

      if (mappedDetails.length > 0) {
        console.log('   Mapped products:');
        mappedDetails.slice(0, 5).forEach(detail => {
          const priceStr = detail.price !== null ? `CHF ${detail.price}` : 'No price';
          console.log(`     - "${detail.name}" (${detail.category}) - ${priceStr}`);
        });
        if (mappedDetails.length > 5) {
          console.log(`     ... and ${mappedDetails.length - 5} more`);
        }
      }

      if (skippedProducts > 0) {
        console.log(`   ⚠️  Skipped: ${skippedProducts} products (relative pricing, included by default, or no category mapping)`);
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    results.productMapping.error = errorMessage;
    console.error(`   ❌ Error: ${errorMessage}`);
  }
  console.log('');

  // Test 4: Rate Plan Mapping (will be updated with real data after Phase 3 tests)
  console.log('💰 Testing Rate Plan Mapping...');
  try {
    // Use placeholder data initially - will be updated with real data after Phase 3 tests
    const testCases: Array<{ rateId: string; rateName: string }> = [];

    const testResults: Array<{
      rateId: string;
      rateName: string;
      mappedTo: string | null;
      isMapped: boolean; // Whether this rate has a manual mapping configured
    }> = [];

    // If no test cases, create empty results (will be populated after Phase 3)
    if (testCases.length === 0) {
      results.ratePlanMapping.success = true;
      results.ratePlanMapping.testCases = [];
      console.log('   ⏳ Waiting for Phase 3 API data to populate real rates...');
    } else {
      for (const testCase of testCases) {
        const mapped = mapMewsRateToLocalRatePlan(testCase.rateId, testCase.rateName);
        // Check if there's a manual mapping configured
        const envMapping = import.meta.env.VITE_MEWS_RATE_MAPPING_JSON;
        let isMapped = false;
        if (envMapping) {
          try {
            const mapping = JSON.parse(envMapping) as Record<string, string>;
            isMapped = mapping[testCase.rateId] !== undefined;
          } catch {
            // Ignore parsing errors
          }
        }
        
        testResults.push({
          rateId: testCase.rateId,
          rateName: testCase.rateName,
          mappedTo: mapped,
          isMapped,
        });
      }

      results.ratePlanMapping.success = true;
      results.ratePlanMapping.testCases = testResults;

      console.log('   ✅ Success');
      testResults.forEach(test => {
        const mappedStr = test.mappedTo || 'null (defaults to standard)';
        const mappingStatus = test.isMapped ? '(manually mapped)' : '(auto-matched)';
        console.log(`     - "${test.rateName}" → ${mappedStr} ${mappingStatus}`);
      });
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    results.ratePlanMapping.error = errorMessage;
    console.error(`   ❌ Error: ${errorMessage}`);
  }

  return results;
}

/**
 * Tests Phase 3: Availability & Pricing APIs
 * 
 * @param configData - Configuration response data
 * @param hotelsData - Hotels response data
 * @returns Phase 3 test results
 */
async function testPhase3Apis(
  configData: ConfigurationResponse | undefined,
  hotelsData: HotelsResponse | undefined
): Promise<TestResults['phase3Tests']> {
  const results: TestResults['phase3Tests'] = {
    availability: { success: false },
    reservationsPricing: { success: false },
    servicesAvailability: { success: false },
    servicesPricing: { success: false },
  };

  if (!configData?.AgeCategories || !hotelsData?.RoomCategories) {
    console.error('   ❌ Missing required data for Phase 3 tests');
    return results;
  }

  // Prepare test dates: February 26, 2026 to February 28, 2026 (2 nights)
  // Note: Mews API requires ISO 8601 UTC format (YYYY-MM-DDTHH:mm:ss.sssZ)
  // The date "26/2/2026" means February 26, 2026 (DD/MM/YYYY format)
  const startDate = new Date('2026-02-26T00:00:00.000Z'); // February 26, 2026, 00:00 UTC
  const endDate = new Date('2026-02-28T00:00:00.000Z');   // February 28, 2026, 00:00 UTC

  // Convert to ISO 8601 UTC format (required by Mews API)
  // Format: YYYY-MM-DDTHH:mm:ss.sssZ (e.g., "2026-02-26T00:00:00.000Z")
  const startUtc = startDate.toISOString();
  const endUtc = endDate.toISOString();
  
  if (import.meta.env.DEV) {
    console.log(`   📅 Test dates: ${startDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`);
    console.log(`   📅 ISO 8601 UTC format: ${startUtc} to ${endUtc}`);
  }

  // Prepare occupancy data (2 adults, 1 child)
  const occupancyData = convertToOccupancies(2, 1, configData.AgeCategories);

  // Test 1: getAvailability
  console.log('🏨 Testing getAvailability()...');
  try {
    const availabilityResponse = await getAvailability({
      startUtc,
      endUtc,
      occupancyData,
    });

    if (availabilityResponse.error) {
      results.availability.error = availabilityResponse.error.Message;
      console.error(`   ❌ Failed: ${availabilityResponse.error.Message}`);
      if (availabilityResponse.error.Code) {
        console.error(`   Code: ${availabilityResponse.error.Code}`);
      }
    } else if (availabilityResponse.data) {
      results.availability.success = true;
      results.availability.data = availabilityResponse.data;
      results.availability.requestParams = {
        startUtc,
        endUtc,
        occupancyData,
      };
      console.log('   ✅ Success');
      
      // Debug: Check what properties exist in the response data
      console.log('   🔍 Response data keys:', Object.keys(availabilityResponse.data || {}));
      console.log('   🔍 RoomCategoryAvailabilities type:', typeof availabilityResponse.data?.RoomCategoryAvailabilities);
      console.log('   🔍 RoomCategoryAvailabilities value:', availabilityResponse.data?.RoomCategoryAvailabilities);
      console.log('   🔍 RoomCategoryAvailabilities isArray:', Array.isArray(availabilityResponse.data?.RoomCategoryAvailabilities));
      
      // Check for RoomCategoryAvailabilities (actual API structure)
      const roomCategoryAvailabilities = availabilityResponse.data?.RoomCategoryAvailabilities || [];
      // Also check legacy RoomCategories for backward compatibility
      const roomCategories = availabilityResponse.data?.RoomCategories || [];
      
      // Use the actual API structure if available, otherwise fall back to legacy
      const availableCount = roomCategoryAvailabilities.length > 0 
        ? roomCategoryAvailabilities.length 
        : roomCategories.length;
      
      console.log(`   Available room categories: ${availableCount}`);
      console.log(`   🔍 RoomCategoryAvailabilities length: ${roomCategoryAvailabilities.length}`);
      console.log(`   🔍 RoomCategories length: ${roomCategories.length}`);
      
      if (roomCategoryAvailabilities.length > 0) {
        console.log('   Sample available rooms (from RoomCategoryAvailabilities):');
        roomCategoryAvailabilities.slice(0, 3).forEach((room) => {
          console.log(`     - RoomCategoryId: ${room.RoomCategoryId.substring(0, 8)}... Available: ${room.AvailableRoomCount}`);
        });
      } else if (roomCategories.length > 0) {
        console.log('   Sample available rooms (from RoomCategories):');
        roomCategories.slice(0, 3).forEach((room) => {
          console.log(`     - RoomCategoryId: ${room.RoomCategoryId.substring(0, 8)}... Available: ${room.AvailableCount}`);
        });
      }
    } else {
      results.availability.error = 'No data returned';
      console.error('   ❌ No data returned');
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    results.availability.error = errorMessage;
    console.error(`   ❌ Error: ${errorMessage}`);
  }
  console.log('');

  // Test 2: getReservationsPricing (if we have a room category from availability)
  const roomCategoryAvailabilities = results.availability.data?.RoomCategoryAvailabilities || [];
  const roomCategories = results.availability.data?.RoomCategories || [];
  const availableRooms = roomCategoryAvailabilities.length > 0 ? roomCategoryAvailabilities : roomCategories;
  
  if (results.availability.success && availableRooms.length > 0) {
    console.log('💰 Testing getReservationsPricing()...');
    try {
      const firstRoomCategory = availableRooms[0];
      // Both RoomCategoryAvailability and AvailabilityItem have RoomCategoryId
      const roomCategoryId = (firstRoomCategory as { RoomCategoryId: string }).RoomCategoryId;

      const pricingResponse = await getReservationsPricing({
        startUtc,
        endUtc,
        roomCategoryId,
        occupancies: [{ OccupancyData: occupancyData as OccupancyData[] }], // Occupancies must be array of Occupancy objects with OccupancyData
      });

      if (pricingResponse.error) {
        results.reservationsPricing.error = pricingResponse.error.Message;
        console.error(`   ❌ Failed: ${pricingResponse.error.Message}`);
        if (pricingResponse.error.Code) {
          console.error(`   Code: ${pricingResponse.error.Code}`);
        }
        if (pricingResponse.error.Details) {
          console.error(`   Details:`, pricingResponse.error.Details);
          // If Details contains the raw response, show it
          if (pricingResponse.error.Details.responseBody) {
            console.error(`   Raw API Response:`, pricingResponse.error.Details.responseBody);
          }
        }
        // Also log the full response for debugging
        console.error(`   Full Error Object:`, pricingResponse.error);
        console.error(`   Response Status:`, pricingResponse.status);
      } else if (pricingResponse.data) {
        results.reservationsPricing.success = true;
        results.reservationsPricing.data = pricingResponse.data;
        results.reservationsPricing.requestParams = {
          startUtc,
          endUtc,
          roomCategoryId,
          occupancies: [{ OccupancyData: occupancyData as OccupancyData[] }],
        };
        console.log('   ✅ Success');
        const roomCategories = pricingResponse.data.RoomCategories || [];
        console.log(`   Pricing for ${roomCategories.length} room category(ies)`);
        if (roomCategories.length > 0 && roomCategories[0].RatePlans) {
          console.log(`   Available rate plans: ${roomCategories[0].RatePlans.length}`);
          if (pricingResponse.data.TotalPrice) {
            console.log(`   Total price: ${pricingResponse.data.Currency || 'CHF'} ${pricingResponse.data.TotalPrice}`);
          }
        }
      } else {
        results.reservationsPricing.error = 'No data returned';
        console.error('   ❌ No data returned');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      results.reservationsPricing.error = errorMessage;
      console.error(`   ❌ Error: ${errorMessage}`);
    }
  } else {
    results.reservationsPricing.error = 'Skipped: No available rooms from getAvailability';
    console.log('   ⏭️  Skipped: No available rooms to test pricing');
  }
  console.log('');

  // Test 3: getServicesAvailability (if we have products/services)
  if (hotelsData.Products && hotelsData.Products.length > 0) {
    console.log('🛍️  Testing getServicesAvailability()...');
    try {
      // Get ServiceId from configuration (if available)
      // Note: ServiceId might be in ConfigurationResponse, but for now we'll skip if not available
      // The actual ServiceId should come from the Configuration API response
      // For testing, we'll need to check if ServiceId is available in configData
      
      // Extract ServiceId from Configurations array
      // ServiceId identifies which "service" (like "Accommodation") the configuration is bound to
      // It's used for getServicesAvailability/getServicesPricing to get add-on/service pricing
      const serviceId = configData?.Configurations?.[0]?.ServiceId;
      
      if (!serviceId) {
        results.servicesAvailability.error = 'ServiceId not available in configuration';
        console.log('   ⏭️  Skipped: ServiceId not available in configuration');
        if (configData?.Configurations && configData.Configurations.length > 0) {
          console.log(`   🔍 Configurations array exists with ${configData.Configurations.length} item(s), but ServiceId is missing`);
        } else {
          console.log('   🔍 Configurations array not found in response');
        }
      } else {
        const servicesAvailabilityResponse = await getServicesAvailability({
          startUtc,
          endUtc,
          serviceId,
        });

        if (servicesAvailabilityResponse.error) {
          results.servicesAvailability.error = servicesAvailabilityResponse.error.Message;
          console.error(`   ❌ Failed: ${servicesAvailabilityResponse.error.Message}`);
        } else if (servicesAvailabilityResponse.data) {
          results.servicesAvailability.success = true;
          results.servicesAvailability.data = servicesAvailabilityResponse.data;
          results.servicesAvailability.requestParams = {
            startUtc,
            endUtc,
            serviceId,
          };
          console.log('   ✅ Success');
          const availability = servicesAvailabilityResponse.data.Availability || [];
          console.log(`   Availability entries: ${availability.length}`);
        } else {
          results.servicesAvailability.error = 'No data returned';
          console.error('   ❌ No data returned');
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      results.servicesAvailability.error = errorMessage;
      console.error(`   ❌ Error: ${errorMessage}`);
    }
  } else {
    results.servicesAvailability.error = 'Skipped: No products available';
    console.log('   ⏭️  Skipped: No products available to test');
  }
  console.log('');

  // Test 4: getServicesPricing (if we have products/services)
  if (hotelsData.Products && hotelsData.Products.length > 0) {
    console.log('💵 Testing getServicesPricing()...');
    try {
      // Extract ServiceId from Configurations array
      // ServiceId identifies which "service" (like "Accommodation") the configuration is bound to
      const serviceId = configData?.Configurations?.[0]?.ServiceId;
      
      if (!serviceId) {
        results.servicesPricing.error = 'ServiceId not available in configuration';
        console.log('   ⏭️  Skipped: ServiceId not available in configuration');
      } else {
        const servicesPricingResponse = await getServicesPricing({
          startUtc,
          endUtc,
          serviceId,
          currencyCode: 'CHF', // Default to CHF
          occupancyData: occupancyData as OccupancyData[],
        });

        if (servicesPricingResponse.error) {
          results.servicesPricing.error = servicesPricingResponse.error.Message;
          console.error(`   ❌ Failed: ${servicesPricingResponse.error.Message}`);
        } else if (servicesPricingResponse.data) {
          results.servicesPricing.success = true;
          results.servicesPricing.data = servicesPricingResponse.data;
          results.servicesPricing.requestParams = {
            startUtc,
            endUtc,
            serviceId,
          };
          console.log('   ✅ Success');
          const pricing = servicesPricingResponse.data.Pricing || [];
          console.log(`   Pricing entries: ${pricing.length}`);
        } else {
          results.servicesPricing.error = 'No data returned';
          console.error('   ❌ No data returned');
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      results.servicesPricing.error = errorMessage;
      console.error(`   ❌ Error: ${errorMessage}`);
    }
  } else {
    results.servicesPricing.error = 'Skipped: No products available';
    console.log('   ⏭️  Skipped: No products available to test');
  }
  console.log('');

  return results;
}

/**
 * Updates rate plan mapping test with real rate data from Phase 3 API responses
 * 
 * @param mappingTests - Mapping tests results to update
 * @param phase3Tests - Phase 3 test results containing availability/pricing data
 */
async function updateRatePlanMappingWithRealData(
  mappingTests: TestResults['mappingTests'],
  phase3Tests: NonNullable<TestResults['phase3Tests']>
): Promise<void> {
  if (!mappingTests?.ratePlanMapping) {
    return;
  }

  try {
    const rateMap = new Map<string, { rateId: string; rateName: string }>();

    // Extract rates from availability response - Rates array
    if (phase3Tests.availability.data?.Rates) {
      phase3Tests.availability.data.Rates.forEach((rate) => {
        if (rate.Id) {
          // Get localized name
          const getName = (name: string | Record<string, string>): string => {
            if (typeof name === 'string') return name;
            if (typeof name === 'object' && name !== null) {
              const nameObj = name as Record<string, string>;
              return nameObj['en-US'] || nameObj['en'] || Object.values(nameObj)[0] || 'Unknown';
            }
            return 'Unknown';
          };
          const rateName = getName(rate.Name);
          rateMap.set(rate.Id, { rateId: rate.Id, rateName });
        }
      });
    }

    // Extract rates from availability response - RoomCategoryAvailabilities -> Pricing -> RateId
    if (phase3Tests.availability.data?.RoomCategoryAvailabilities) {
      phase3Tests.availability.data.RoomCategoryAvailabilities.forEach((roomAvail) => {
        if (roomAvail.RoomOccupancyAvailabilities) {
          roomAvail.RoomOccupancyAvailabilities.forEach((occAvail) => {
            if (occAvail.Pricing) {
              occAvail.Pricing.forEach((pricing) => {
                if (pricing.RateId && !rateMap.has(pricing.RateId)) {
                  // Try to find rate name from Rates array
                  let rateName = 'Unknown';
                  if (phase3Tests.availability.data?.Rates) {
                    const rate = phase3Tests.availability.data.Rates.find((r) => r.Id === pricing.RateId);
                    if (rate?.Name) {
                      const getName = (name: string | Record<string, string>): string => {
                        if (typeof name === 'string') return name;
                        if (typeof name === 'object' && name !== null) {
                          const nameObj = name as Record<string, string>;
                          return nameObj['en-US'] || nameObj['en'] || Object.values(nameObj)[0] || 'Unknown';
                        }
                        return 'Unknown';
                      };
                      rateName = getName(rate.Name);
                    }
                  }
                  rateMap.set(pricing.RateId, { rateId: pricing.RateId, rateName });
                }
              });
            }
          });
        }
      });
    }

    // Extract rates from reservations pricing response - RoomCategories -> RatePlans -> Id
    if (phase3Tests.reservationsPricing.data?.RoomCategories) {
      phase3Tests.reservationsPricing.data.RoomCategories.forEach((roomCat) => {
        if (roomCat.RatePlans) {
          roomCat.RatePlans.forEach((ratePlan) => {
            if (ratePlan.Id && !rateMap.has(ratePlan.Id)) {
              const getName = (name: string | Record<string, string>): string => {
                if (typeof name === 'string') return name;
                if (typeof name === 'object' && name !== null) {
                  const nameObj = name as Record<string, string>;
                  return nameObj['en-US'] || nameObj['en'] || Object.values(nameObj)[0] || 'Unknown';
                }
                return 'Unknown';
              };
              const rateName = getName(ratePlan.Name);
              rateMap.set(ratePlan.Id, { rateId: ratePlan.Id, rateName });
            }
          });
        }
      });
    }

    // Convert map to array and test each rate
    const testResults: Array<{
      rateId: string;
      rateName: string;
      mappedTo: string | null;
      isMapped: boolean;
    }> = [];

    // Check for manual mapping configuration
    const envMapping = import.meta.env.VITE_MEWS_RATE_MAPPING_JSON;
    let manualMappings: Record<string, string> = {};
    if (envMapping) {
      try {
        manualMappings = JSON.parse(envMapping) as Record<string, string>;
      } catch {
        // Ignore parsing errors
      }
    }

    // Sort rates by name for consistent display
    const sortedRates = Array.from(rateMap.values()).sort((a, b) => 
      a.rateName.localeCompare(b.rateName)
    );

    for (const rate of sortedRates) {
      const mapped = mapMewsRateToLocalRatePlan(rate.rateId, rate.rateName);
      const isMapped = manualMappings[rate.rateId] !== undefined;
      
      testResults.push({
        rateId: rate.rateId,
        rateName: rate.rateName,
        mappedTo: mapped,
        isMapped,
      });
    }

    // Update the mapping test results
    mappingTests.ratePlanMapping.success = true;
    mappingTests.ratePlanMapping.testCases = testResults;

    console.log(`   ✅ Updated with ${testResults.length} real rate(s) from API`);
    const mappedCount = testResults.filter(r => r.isMapped).length;
    const unmappedCount = testResults.length - mappedCount;
    console.log(`   📊 Mapped: ${mappedCount}, Unmapped: ${unmappedCount}`);
    
    if (testResults.length > 0) {
      console.log('   Sample rates:');
      testResults.slice(0, 5).forEach(test => {
        const mappedStr = test.mappedTo || 'standard (default)';
        const mappingStatus = test.isMapped ? '✅ manually mapped' : '⚠️  auto-matched';
        console.log(`     - "${test.rateName}" → ${mappedStr} (${mappingStatus})`);
      });
      if (testResults.length > 5) {
        console.log(`     ... and ${testResults.length - 5} more`);
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error(`   ❌ Error updating rate plan mapping: ${errorMessage}`);
  }
}

// Export for use in browser console or other contexts
if (typeof window !== 'undefined') {
  (window as unknown as { testMewsApi?: typeof testMewsApiConnection }).testMewsApi = testMewsApiConnection;
}
